/**
 * WorkflowStepScope (Step 2)
 *
 * Scope & permit type confirmation.
 * Substeps: trade, jobScope, permitType, override
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Wrench, FileText, Check, AlertTriangle, Zap, Droplets, Flame, Wind, HardHat } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { UseJobWorkflowReturn } from '@/hooks/useJobWorkflow';
import type { JobData } from '../WorkflowContainer';

interface WorkflowStepScopeProps {
  jobData: JobData;
  workflow: UseJobWorkflowReturn;
  onUpdate?: (updates: Partial<JobData>) => void;
}

// Trade icons mapping
const tradeIcons: Record<string, React.ReactNode> = {
  electrical: <Zap className="h-5 w-5" />,
  plumbing: <Droplets className="h-5 w-5" />,
  mechanical: <Wind className="h-5 w-5" />,
  gas: <Flame className="h-5 w-5" />,
  general: <HardHat className="h-5 w-5" />,
};

// Common trades
const TRADES = [
  { code: 'electrical', name: 'Electrical', description: 'Panels, circuits, wiring, lighting' },
  { code: 'plumbing', name: 'Plumbing', description: 'Pipes, fixtures, water heaters' },
  { code: 'mechanical', name: 'Mechanical/HVAC', description: 'AC, heating, ventilation' },
  { code: 'general', name: 'Building/General', description: 'Structural, framing, roofing' },
  { code: 'gas', name: 'Gas', description: 'Gas lines and appliances' },
];

export function WorkflowStepScope({ jobData, workflow, onUpdate }: WorkflowStepScopeProps) {
  const currentSubstep = workflow.currentSubstepId;
  const [editingTrade, setEditingTrade] = useState(false);
  const [editingJobScope, setEditingJobScope] = useState(!jobData.jobScope);

  // Ref to prevent re-render cascades (workflow object is new on every render)
  const lastSubstepsRef = useRef<string>('');

  // Mark substeps as complete when data exists
  useEffect(() => {
    const substepKey = [
      (jobData.trade || jobData.permitType) ? 'trade' : '',
      jobData.jobScope ? 'jobScope' : '',
      jobData.permitType ? 'permitType' : '',
    ].join(',');

    if (lastSubstepsRef.current !== substepKey) {
      lastSubstepsRef.current = substepKey;
      if (jobData.trade || jobData.permitType) workflow.markSubstepComplete('scope', 'trade');
      if (jobData.jobScope) workflow.markSubstepComplete('scope', 'jobScope');
      if (jobData.permitType) workflow.markSubstepComplete('scope', 'permitType');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobData]);

  const handleUpdate = (field: string, value: unknown) => {
    if (onUpdate) {
      onUpdate({ [field]: value });
    }
  };

  return (
    <div className="space-y-6">
      {/* Trade Selection */}
      <Card className={cn(currentSubstep === 'trade' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Trade <span className="text-destructive">*</span>
            {(jobData.trade || jobData.permitType) && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Selected
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(jobData.trade || jobData.permitType) && !editingTrade ? (
            <div className="space-y-3">
              {(() => {
                const tradeCode = jobData.trade || jobData.permitType;
                const selectedTrade = TRADES.find(t => t.code === tradeCode);
                return (
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                    <div className="p-2 rounded-lg bg-primary text-primary-foreground">
                      {tradeIcons[tradeCode || ''] || <Wrench className="h-5 w-5" />}
                    </div>
                    <div>
                      <p className="font-medium">{selectedTrade?.name || tradeCode}</p>
                      <p className="text-sm text-muted-foreground">{selectedTrade?.description}</p>
                    </div>
                  </div>
                );
              })()}
              <ConfidenceBadge level="high" label="Set during job creation" />
              <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-blue-400 hover:text-blue-300" onClick={() => setEditingTrade(true)}>
                Change trade
              </Button>
            </div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">
                Select the primary trade for this permit application.
              </p>
              <RadioGroup
                value={jobData.trade || jobData.permitType || ''}
                onValueChange={(value) => {
                  handleUpdate('trade', value);
                  handleUpdate('permitType', value);
                  setEditingTrade(false);
                }}
                className="grid gap-3"
              >
                {TRADES.map((trade) => (
                  <Label
                    key={trade.code}
                    htmlFor={`trade-${trade.code}`}
                    className={cn(
                      'flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors',
                      'hover:bg-muted/50',
                      (jobData.trade === trade.code || jobData.permitType === trade.code) &&
                        'border-primary bg-primary/5'
                    )}
                  >
                    <RadioGroupItem value={trade.code} id={`trade-${trade.code}`} />
                    <div
                      className={cn(
                        'p-2 rounded-lg',
                        (jobData.trade === trade.code || jobData.permitType === trade.code)
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      )}
                    >
                      {tradeIcons[trade.code] || <Wrench className="h-5 w-5" />}
                    </div>
                    <div>
                      <span className="font-medium">{trade.name}</span>
                      <p className="text-sm text-muted-foreground">{trade.description}</p>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </>
          )}
        </CardContent>
      </Card>

      {/* Job Scope */}
      <Card className={cn(currentSubstep === 'jobScope' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Job Scope <span className="text-destructive">*</span>
            {jobData.jobScope && (
              <Badge variant="outline" className="ml-auto text-green-600 border-green-300 dark:text-green-300 dark:border-green-700">
                <Check className="h-3 w-3 mr-1" />
                Defined
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Describe the specific work being performed. This helps determine document requirements.
            </p>
            {jobData.jobScope && !editingJobScope ? (
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="font-medium">{jobData.jobScope}</p>
                <Button variant="link" size="sm" className="h-auto p-0 mt-2 text-blue-400 hover:text-blue-300" onClick={() => setEditingJobScope(true)}>
                  Change scope
                </Button>
              </div>
            ) : (
              <Textarea
                placeholder="e.g., Panel upgrade from 100A to 200A, or HVAC system replacement"
                value={jobData.jobScope || ''}
                onChange={(e) => handleUpdate('jobScope', e.target.value)}
                onBlur={() => {
                  if (jobData.jobScope) {
                    setEditingJobScope(false);
                  }
                }}
                rows={3}
                autoFocus={editingJobScope && !!jobData.jobScope}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permit Type Confirmation */}
      <Card className={cn(currentSubstep === 'permitType' && 'ring-2 ring-primary')}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Permit Type Recommendation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {jobData.trade && (
              <div className="p-4 rounded-lg border-2 border-primary/20 bg-primary/5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium capitalize">
                      {jobData.trade} Permit
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Based on your selected trade and scope
                    </p>
                  </div>
                  <ConfidenceBadge level="high" />
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <Checkbox id="override" className="mt-0.5" />
              <div>
                <Label htmlFor="override" className="text-sm font-medium cursor-pointer">
                  Override recommendation
                </Label>
                <p className="text-xs text-muted-foreground">
                  I need a different permit type than recommended
                </p>
              </div>
            </div>

            {!jobData.trade && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Select a trade first to see permit recommendations
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
